import React, { useState, useEffect } from "react";
import { Button } from "@component/button";
import {
  EditIcon,
  BriefcaseIcon,
  GraduationCapIcon,
  AwardIcon,
  CodeIcon,
  FileTextIcon,
  PlusIcon,
  XIcon,
  UserIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  GraduationCap,
  Code,
  CheckSquare,
  Trophy,
} from "lucide-react";
import { CVData, Education } from "@type/cv"
import { getMyCV } from "@service/cv";
import { ResumeUpload } from "@component/resume-upload";
import { ResumeAndFilesUpload } from "@component/resume-and-files-upload";
import { Input } from "./input";
import { Textarea } from "./textarea";
import { Checkbox } from "./checkbox";
import { cn } from "@util/tailwind";
import { getCookie } from "@util/cookie";
import CountryCodeSelect from "./country-code-select";
import { COUNTRY_CODES, CountryCode } from "./country-codes-data";
import ExperienceTag, { filterTagsByContext, TagType } from "./experience-tag";

interface JobseekerConfirmDetailsProps {
  onSave: (profile: CVData) => void;
  isLoading?: boolean;
}

// Default data structure to use when formData is null
const defaultFormData: CVData = {
  name: "",
  position: "",
  professional_summary: "",
  email: "",
  country_code: "+1",
  phone: "",
  location: "",
  avatar: "",
  skills: [],
  work_experience: [],
  education: [],
  certifications: [],
  awards: [],
  publications: [],
  projects: []
};

export function JobseekerConfirmDetails({
  onSave,
  isLoading,
}: JobseekerConfirmDetailsProps) {
  const dataFormApply = getCookie("__apply_job_guest__");
  const [isEditing, setIsEditing] = useState(false);
  const [newSkill, setNewSkill] = useState("");
  const [formData, setFormData] = useState<CVData | undefined>(undefined);
  const [submitted, setSubmitted] = useState(false);
  const [isLoadingCV, setLoadingCV] = useState<boolean>(false);
  // Captures the most recent /cv/my error so the empty-state UI can
  // surface a real message instead of a generic "no profile data" line.
  const [loadCVError, setLoadCVError] = useState<string | null>(null);
  const [education, setEducation] = useState<Education[]>([]);
  const [newTech, setNewTech] = useState("");
  const [currentTipIndex, setCurrentTipIndex] = useState<number>(0);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(COUNTRY_CODES.find((country) => country.code === dataFormApply?.country_code || "") || {
    code: "+1",
    name: "United States",
    flag: "🇺🇸",
  });

  const onLoadCV = async () => {
    setLoadingCV(true);
    setLoadCVError(null);
    const response = await getMyCV()
    if (response.code === 200 && response?.data) {
      const data = response?.data?.CV_data;
      setFormData({
        ...data,
        country_code: selectedCountry.code || data.country_code,
      });
      if (!dataFormApply) {
        setSelectedCountry(COUNTRY_CODES.find((country) => country.code === data.country_code) || selectedCountry);
      }
      setEducation(data.education || []);
    } else {
      setFormData(undefined);
      const apiMessage =
        (response?.errors && response.errors.length > 0 && response.errors.join(" ")) ||
        response?.message ||
        "We couldn't load your profile.";
      setLoadCVError(apiMessage);
    }

    setLoadingCV(false);
  }

  useEffect(() => {
    if (isLoadingCV || isLoading) {
      // Start a timer to change the tip every 5 seconds
      let interval: NodeJS.Timeout | undefined;
      if (currentTipIndex < tips.length - 1) {
        interval = setInterval(() => {
          setCurrentTipIndex((prevIndex) => {
            if (prevIndex < tips.length - 1) {
              return prevIndex + 1;
            } else {
              clearInterval(interval);
              return prevIndex;
            }
          });
        }, 3000);
      }

      return () => clearInterval(interval);
    }
  }, [isLoading, isLoadingCV]);

  const reloadCV = async () => {
    setFormData(defaultFormData);
    setLoadingCV(true);
    setLoadCVError(null);
    const response = await getMyCV()
    if (response.code === 200 && response?.data) {
      const data = response?.data?.CV_data;
      setFormData({
        ...data,
        country_code: selectedCountry.code || data.country_code,
      });
      setEducation(data.education || []);
    } else {
      setFormData(undefined);
      const apiMessage =
        (response?.errors && response.errors.length > 0 && response.errors.join(" ")) ||
        response?.message ||
        "We couldn't load your profile.";
      setLoadCVError(apiMessage);
    }

    setLoadingCV(false);
  }

  useEffect(() => {
    if (!isLoading) {
      onLoadCV()
    }
  }, [isLoading])

  useEffect(() => {
    setFormData((prev) => {
      if (!prev) return defaultFormData;
      return {
        ...prev,
        education,
      };
    });
  }, [education]);

  const handleCountryCodeChange = (countryCode: CountryCode) => {
    setFormData((prev) => {
      if (!prev) return defaultFormData;
      return {
        ...prev,
        country_code: countryCode.code,
      } as CVData;
    });

    setSelectedCountry(countryCode);
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      return {
        ...prev,
        [name]: value,
      } as CVData;
    });
  };

  // Handle changes for nested objects (work experience, projects, etc.)
  const handleNestedChange = (
    section: string,
    index: number,
    field: string,
    value: string,
  ) => {
    setFormData((prev) => {
      if (!prev) return defaultFormData;
      // Map component section names to CVData property names
      const sectionMappings: Record<string, keyof CVData> = {
        workExperience: 'work_experience',
        projects: 'projects',
        publications: 'publications',
        awards: 'awards'
      };

      const sectionKey = sectionMappings[section] || section as keyof CVData;
      const updatedSection = [...(prev[sectionKey] as any[])];
      updatedSection[index] = {
        ...updatedSection[index],
        [field]: value,
      };
      return {
        ...prev,
        [sectionKey]: updatedSection,
      };
    });
  };

  const toggleCurrentlyWorking = (index: number, value: boolean) => {
    setFormData((prev) => {
      if (!prev) return defaultFormData;
      const updatedExperiences = [...prev.work_experience];
      updatedExperiences[index].is_current = value;
      updatedExperiences[index].period_end_year = updatedExperiences[index].is_current
        ? 9999
        : updatedExperiences[index].period_end_year;
      updatedExperiences[index].period_end_month = updatedExperiences[index].is_current
        ? 12
        : updatedExperiences[index].period_end_month;
      return {
        ...prev,
        work_experience: updatedExperiences
      };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsEditing(false);
  };

  const handleAddSkill = () => {
    if (newSkill.trim()) {
      setFormData((prev) => {
        if (!prev) return defaultFormData;
        return {
          ...prev,
          skills: [...prev.skills, newSkill.trim()],
        };
      });
      setNewSkill("");
    }
  };

  const handleRemoveSkill = (index: number) => {
    setFormData((prev) => {
      if (!prev) return defaultFormData;
      return {
        ...prev,
        skills: prev.skills.filter((_, i) => i !== index),
      }
    });
  };

  const handleAddSection = (section: string) => {
    setFormData((prev) => {
      if (!prev) return defaultFormData;

      // Map component section names to CVData property names
      const sectionMappings: Record<string, keyof CVData> = {
        workExperience: 'work_experience',
        projects: 'projects',
        publications: 'publications',
        awards: 'awards'
      };

      const sectionKey = sectionMappings[section] || section as keyof CVData;
      const sectionData = [...(prev[sectionKey] as any[])];

      let newItem;
      switch (section) {
        case "workExperience":
          newItem = {
            institution: "",
            role: "",
            duration: "",
            description: "",
            period: "",
            period_start_month: 0,
            period_start_year: 0,
            period_end_month: 0,
            period_end_year: 9999,
          };
          break;
        case "certifications":
          newItem = {
            name: "",
            institution: "",
            date: "",
          };
          break;
        case "education":
          newItem = {
            degree: "",
            institution: "",
            period: "",
            location: "",
            description: "",
          };
          break;
        case "projects":
          newItem = {
            name: "",
            role: "",
            year: "",
            github_link: "",
            live_demo: "",
            description: "",
            tech_stack: []
          };
          break;
        case "publications":
          newItem = {
            title: "",
            publisher: "",
            year: "",
          };
          break;
        case "awards":
          newItem = {
            name: "",
            institution: "",
            date: "",
          };
          break;
        default:
          newItem = {};
      }

      return {
        ...prev,
        [sectionKey]: [...sectionData, newItem],
      };
    });
  };

  const handleRemoveSection = (section: string, index: number) => {
    setFormData((prev) => {
      if (!prev) return defaultFormData;
      // Map component section names to CVData property names
      const sectionMappings: Record<string, keyof CVData> = {
        workExperience: 'work_experience',
        projects: 'projects',
        publications: 'publications',
        awards: 'awards',
        education: 'education',
        cerfifications: "certifications"
      };

      const sectionKey = sectionMappings[section] || section as keyof CVData;
      const sectionData = [...(prev[sectionKey] as any[])];
      return {
        ...prev,
        [sectionKey]: sectionData.filter((_, i) => i !== index),
      };
    });
  };

  const addTechnology = (index: number) => {
    if (newTech.trim()) {
      setFormData((prev) => {
        if (!prev) return defaultFormData;
        const updatedProjects = [...prev.projects];
        updatedProjects[index].tech_stack.push(newTech.trim());
        return {
          ...prev,
          projects: updatedProjects
        };
      });
      setNewTech("");
    }
  };

  const removeTechnology = (projectIndex: number, techIndex: number) => {
    setFormData((prev) => {
      if (!prev) return defaultFormData;
      const updatedProjects = [...prev.projects];
      updatedProjects[projectIndex].tech_stack.splice(techIndex, 1);
      return {
        ...prev,
        projects: updatedProjects
      };
    });
  };

  const toggleExpiry = (index: number) => {
    setFormData((prev) => {
      if (!prev) return defaultFormData;
      const updatedCertifications = [...prev.certifications];
      if (
        updatedCertifications[index]?.expiration_date ||
        updatedCertifications[index]?.expiration_date === ""
      ) {
        updatedCertifications[index].expiration_date = undefined;
      } else {
        updatedCertifications[index].expiration_date = "";
      }
      return {
        ...prev,
        certifications: updatedCertifications
      };
    });
  };

  // Create a mapped version of formData for the UI to use based on component expectations
  const displayData = {
    name: formData?.name,
    email: dataFormApply?.email || formData?.email,
    country_code: formData?.country_code,
    phone: formData?.phone || dataFormApply?.phone,
    location: formData?.location,
    position: formData?.position,
    experience: formData?.professional_summary,
    education: formData?.education || [],
    skills: formData?.skills || [],
    workExperience: formData?.work_experience || [],
    projects: formData?.projects || [],
    publications: formData?.publications || [],
    awards: formData?.awards || [],
    certifications: formData?.certifications || [],
  };

  let isDisabledButton = false;
  if (displayData.name === "" || displayData.email === "") {
    isDisabledButton = true;
  }

  if (displayData.workExperience.length > 0) {
    for (let i = 0; i < displayData.workExperience.length; i++) {
      const experience = displayData.workExperience[i];
      if (!experience.role || !experience.institution || !experience.period_start_year || (!experience.is_current && experience.period_end_year === 0) || (experience.period_end_year < experience.period_start_year)) {
        isDisabledButton = true;
        break;
      }
    }
  }

  if (displayData.education.length > 0) {
    for (let i = 0; i < displayData.education.length; i++) {
      const education = displayData.education[i];
      if (!education.degree || !education.institution || !education.period) {
        isDisabledButton = true;
        break;
      }
    }
  }

  if (displayData.awards.length > 0) {
    for (let i = 0; i < displayData.awards.length; i++) {
      const award = displayData.awards[i];
      if (!award.name || !award.institution || !award.date) {
        isDisabledButton = true;
        break;
      }
    }
  }

  if (displayData.projects.length > 0) {
    for (let i = 0; i < displayData.projects.length; i++) {
      const project = displayData.projects[i];
      if (!project.name || !project.role || !project.year || !project.github_link || !project.live_demo) {
        isDisabledButton = true;
        break;
      }
    }
  }

  if (displayData.certifications.length > 0) {
    for (let i = 0; i < displayData.certifications.length; i++) {
      const certification = displayData.certifications[i];
      if (!certification.name || !certification.institution || !certification.date) {
        isDisabledButton = true;
        break;
      }
    }
  }

  const tips: string[] = [
    "This should take less than a minute...",
    "Analyzing your career highlights...",
    "Extracting your core competencies...",
    "Mapping your expertise to market opportunities...",
    "Validating your professional achievements...",
    "Finalizing your AI-optimized candidate profile..."
  ];


  // If there's no profile data, fall back to a re-upload form so the
  // user can retry. The API's error message is surfaced verbatim so the
  // user sees the actual cause (e.g. "Resume could not be parsed").
  // After a successful re-upload, sessionStorage.cv_uuid is refreshed
  // by ResumeUpload and we kick off onLoadCV() again.
  if (!formData) {
    return (
      <div className="w-full bg-white dark:bg-gray-900 rounded-xl p-6 md:p-8">
        <div className="flex flex-col items-center text-center mb-6">
          <h3 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
            We couldn't load your profile.
          </h3>
          {loadCVError && (
            <p className="mt-2 text-sm md:text-md text-red-600 dark:text-red-400">
              {loadCVError}
            </p>
          )}
          <p className="mt-2 text-sm md:text-md text-gray-600 dark:text-gray-400">
            Please re-upload your CV file below. We'll try parsing it again automatically.
          </p>
        </div>
        {import.meta.env.VITE_MODE_DEMO_PRACTICE_INTERVIEW === "true" ? (
          <ResumeAndFilesUpload
            onUploadComplete={() => {
              onLoadCV();
            }}
          />
        ) : (
          <ResumeUpload
            onUploadComplete={() => {
              onLoadCV();
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="w-full bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-4 md:p-8 -mt-10">
      {(isLoading || isLoadingCV) ? (
        <div className="flex items-center mb-6 justify-center">
          <h2
            className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white"
          >
            Processing Your Details
          </h2>
        </div>
      ) : (
        <div className="flex items-center mb-1 justify-between">
          <h2
            className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white"
          >
            {submitted ? "Submitting Your Details" : "Confirm Your Details"}
          </h2>

          {(!isEditing && (!isLoading && !isLoadingCV)) ? (
            <Button
              onClick={() => { setIsEditing(true); }}
              variant="outline"
              className="flex items-center gap-2"
            >
              <EditIcon className="h-4 w-4" />
              <span className="hidden md:block">Edit</span>
            </Button>
          ) : null}
        </div>
      )}

      <p className={cn("text-gray-600 dark:text-gray-400 text-sm md:text-md mb-9", {
        "text-center": isLoading || isLoadingCV,
      })}>
        {(isLoading || isLoadingCV) ? (submitted) ?
          "Submitting your details, please wait..." :
          (
            <>
              {tips[currentTipIndex]}
            </>
          ) :
          (
            <>
              We've extracted the following information <br className="block md:hidden" /> from your resume.
              <br />Please review and make any necessary corrections.
            </>
          )
        }
      </p>
      {isLoading || isLoadingCV ?
        <div className="flex flex-col items-center justify-center py-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600" />
        </div>
        :
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Personal Background Section */}
            <div className="md:col-span-2 mb-2 md:mb-4">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-1 md:gap-2 mb-3">
                <UserIcon className="h-5 w-5 text-blue-500" />
                Personal Background
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-black font-bold dark:text-gray-300 mb-1">
                    Full Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="name"
                      value={displayData.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-600 text-sm md:text-md dark:text-white">
                      {displayData.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-bold text-black dark:text-gray-300 mb-1">
                    Email
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      name="email"
                      value={displayData.email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-600 text-sm md:text-md dark:text-white">
                      {displayData.email}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-bold text-black dark:text-gray-300 mb-1">
                    Phone
                  </label>
                  {isEditing ? (
                    <div className="flex">
                      <CountryCodeSelect
                        value={selectedCountry}
                        onChange={handleCountryCodeChange}
                      />
                      <Input
                        id="phone"
                        name="phone"
                        placeholder="Phone number*"
                        value={displayData.phone}
                        type="tel"
                        maxLength={18}
                        className="rounded-l-none"
                        onChange={handleChange}
                      />
                    </div >
                  ) : (
                    <p className="text-gray-600 text-sm md:text-md dark:text-white">
                        {(displayData.phone?.length ?? 0) === 0 ? "No phone number provided" : `${displayData.country_code || "+1"}${displayData.phone}`}
                    </p>
                  )
                  }
                </div >

                <div>
                  <label className="block text-sm font-bold text-black dark:text-gray-300 mb-1">
                    Location
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="location"
                      value={displayData.location}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-600 text-sm md:text-md dark:text-white">
                      {displayData.location}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-bold text-black dark:text-gray-300 mb-1">
                    Job Title
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="position"
                      value={displayData.position}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-600 text-sm md:text-md dark:text-white">
                      {displayData.position}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-bold text-black dark:text-gray-300 mb-1">
                    Profile Summary
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="experience"
                      value={displayData.experience}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-600 text-sm md:text-md dark:text-white">
                      {displayData.experience}
                    </p>
                  )}
                </div>
              </div >
            </div >

            {/* Divider after Personal Background */}
            < div className="md:col-span-2 border-t border-gray-200 dark:border-gray-700 my-2 md:my-4" ></div >

            {/* Education Section */}
            <div className="md:col-span-2" >
              <div className="flex justify-between items-center">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-1 md:gap-2 mb-3">
                  <GraduationCapIcon className="h-5 w-5 text-blue-500" />
                  Education
                </h3>
                {isEditing && (
                  <Button
                    type="button"
                    onClick={() => handleAddSection("education")}
                    variant="outline"
                    className="flex items-center gap-1 text-sm py-1"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Education
                  </Button>
                )}
              </div>
              {
                isEditing ? (
                  <>
                    {
                      displayData.education.map((education, index: number) => (
                        <div key={index} className="relative flex items-start gap-4 mb-4">
                          <div className="flex-1">
                            <div className="space-y-4" key={index}>
                              <button
                                onClick={() => handleRemoveSection("education", index)}
                                className="float-right text-red-500 hover:text-red-700"
                              >
                                <XIcon
                                  className="h-5 w-5"
                                />
                              </button>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="text-sm text-gray-400 mb-1 block">
                                    Degree
                                  </label>
                                  <Input
                                    name="degree"
                                    value={education.degree}
                                    onChange={(e) =>
                                      handleNestedChange(
                                        "education",
                                        index,
                                        "degree",
                                        e.target.value,
                                      )
                                    }
                                    className="border-gray-300 text-gray-900"
                                  />
                                  {education.degree === "" && (
                                    <div className="flex flex-rowm items-center mt-2 space-x-1">
                                      <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                                      <p className="text-red-600 text-sm">
                                        Degree must be filled in.
                                      </p>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <label className="text-sm text-gray-400 mb-1 block">
                                    Period
                                  </label>
                                  <Input
                                    name="period"
                                    value={education.period}
                                    onChange={(e) =>
                                      handleNestedChange(
                                        "education",
                                        index,
                                        "period",
                                        e.target.value,
                                      )
                                    }
                                    className="border-gray-300 text-gray-900"
                                  />
                                  {education.period === "" && (
                                    <div className="flex flex-rowm items-center mt-2 space-x-1">
                                      <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                                      <p className="text-red-600 text-sm">
                                        Period must be filled in.
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div>
                                <label className="text-sm text-gray-400 mb-1 block">
                                  Institution
                                </label>
                                <Input
                                  name="institution"
                                  value={education.institution}
                                  onChange={(e) =>
                                    handleNestedChange(
                                      "education",
                                      index,
                                      "institution",
                                      e.target.value,
                                    )
                                  }
                                  className="border-gray-300 text-gray-900"
                                />
                                {education.institution === "" && (
                                  <div className="flex flex-rowm items-center mt-2 space-x-1">
                                    <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                                    <p className="text-red-600 text-sm">
                                      Institution must be filled in.
                                    </p>
                                  </div>
                                )}
                              </div>
                              <div>
                                <label className="text-sm text-gray-400 mb-1 block">
                                  Location
                                </label>
                                <Input
                                  name="location"
                                  value={education.location}
                                  onChange={(e) =>
                                    handleNestedChange(
                                      "education",
                                      index,
                                      "location",
                                      e.target.value,
                                    )
                                  }
                                  className="border-gray-300 text-gray-900"
                                />
                              </div>
                              <div>
                                <label className="text-sm text-gray-400 mb-1 block">
                                  Description
                                </label>
                                <Textarea
                                  name="description"
                                  value={education.description}
                                  onChange={(e) =>
                                    handleNestedChange(
                                      "education",
                                      index,
                                      "description",
                                      e.target.value,
                                    )
                                  }
                                  className="border-gray-300 text-gray-900"
                                  rows={3}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    }
                  </>
                ) : (
                  <>
                    {displayData.education.map((edu, index) => (
                      <div key={index} className={cn("relative pl-12", {
                        "pb-8": index < displayData.education.length - 1,
                      })}>
                        {/* Timeline line - only show for non-last items */}
                        {index < displayData.education.length - 1 && (
                          <div className="absolute left-3 top-1 bottom-0 w-[2px] bg-blue-100 dark:bg-blue-900/30" style={{
                            backgroundColor: "#dcfce7",
                          }}></div>
                        )}

                        {/* Icon */}
                        <div className="absolute left-0 top-1">
                          <div
                            className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center z-10 border-2 border-blue-500 dark:border-blue-400"
                            style={{
                              backgroundColor: "#dcfce7",
                              border: "2px solid #22c55e",
                            }}
                          >
                            <GraduationCap
                              className="h-3 w-3 text-blue-600 dark:text-blue-400"
                              style={{
                                color: "#22c55e",
                                border: "0px solid #22c55e",
                              }}
                            />
                          </div>
                        </div>

                        {/* Content */}
                        <div>
                          <div className="mb-1">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1">
                              <h5 className="font-medium text-gray-900 dark:text-gray-100 text-left">
                                {edu.degree}
                                {" "}
                                {edu.tags && edu.tags.length > 0 && (
                                  <>
                                    {filterTagsByContext(
                                      edu.tags as TagType[],
                                      "education"
                                    ).map((tag, tagIndex) => (
                                      <ExperienceTag
                                        key={tagIndex}
                                        type={tag}
                                      />
                                    ))}
                                  </>
                                )}
                              </h5>
                              {edu.period && (
                                <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0">
                                  {edu.period}
                                </span>
                              )}
                            </div>
                          </div>
                          {
                            (edu.institution || edu.location) && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                                {edu.institution}{`${edu.location && ` • ${edu.location}`}`}
                              </p>
                            )
                          }
                          {
                            edu.description && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {edu.description}
                              </p>
                            )
                          }
                        </div >
                      </div >
                    ))
                    }
                  </>
                )}
            </div >

            {/* Divider after Education */}
            < div className="md:col-span-2 border-t border-gray-200 dark:border-gray-700 my-2 md:my-4" ></div >

            {/* Skills Section */}
            < div className="md:col-span-2" >
              <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-1 md:gap-2 mb-3">
                <CodeIcon className="h-5 w-5 text-blue-500" />
                Skills
              </h3>
              {
                isEditing ? (
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {displayData.skills.map((skill, index) => (
                        <div
                          key={index}
                          className="flex items-center bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-3 py-1 rounded-full"
                        >
                          <span>
                            {skill}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveSkill(index)}
                            className="ml-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                          >
                            <XIcon className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newSkill}
                        onChange={(e) => setNewSkill(e.target.value)}
                        placeholder="Add a skill"
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                      />

                      <Button
                        type="button"
                        onClick={handleAddSkill}
                        className="flex items-center gap-1"
                      >
                        <PlusIcon className="h-4 w-4" />
                        Add
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {displayData.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-3 py-1 rounded-full text-sm"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                )
              }
            </div >

            {/* Divider after Skills */}
            < div className="md:col-span-2 border-t border-gray-200 dark:border-gray-700 my-2 md:my-4" ></div >

            {/* Work Experience Section */}
            < div className="md:col-span-2" >
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-md md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <BriefcaseIcon className="h-5 w-5 text-blue-500" />
                  Work Experience
                </h3>
                {isEditing && (
                  <Button
                    type="button"
                    onClick={() => handleAddSection("workExperience")}
                    variant="outline"
                    className="flex items-center gap-1 text-sm py-1"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Experience
                  </Button>
                )}
              </div>

              {
                displayData.workExperience.map((job, index) => (
                  <div key={index}>
                    {isEditing ? (
                      <div className="space-y-3">
                        <button
                          type="button"
                          onClick={() =>
                            handleRemoveSection("workExperience", index)
                          }
                          className="float-right text-red-500 hover:text-red-700"
                        >
                          <XIcon className="h-5 w-5" />
                        </button>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Position
                          </label>
                          <input
                            type="text"
                            value={job.role}
                            onChange={(e) =>
                              handleNestedChange(
                                "workExperience",
                                index,
                                "role",
                                e.target.value,
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                          />
                          {job.role === "" && (
                            <div className="flex flex-rowm items-center mt-2 space-x-1">
                              <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                              <p className="text-red-600 text-sm">
                                Position must be filled in.
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-row space-x-2">
                          <div className="flex-1">
                            <label className="text-sm text-gray-600 mb-1 block">
                              Period Start
                            </label>
                            <div className="flex flex-row space-x-2">
                              <select
                                value={String(job.period_start_year)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm"
                                onChange={(e) =>
                                  handleNestedChange(
                                    "workExperience",
                                    index,
                                    "period_start_year",
                                    e.target.value
                                  )
                                }
                              >
                                <option value="0">Year</option>
                                {Array.from(
                                  { length: new Date().getFullYear() - 1990 + 1 },
                                  (_, i) => new Date().getFullYear() - i
                                ).map((year) => (
                                  <option key={year} value={year.toString()}>
                                    {year}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {job.period_start_year === 0 && (
                              <div className="flex flex-rowm items-center mt-2 space-x-1">
                                <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                                <p className="text-red-600 text-sm">
                                  Position must be filled in.
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <label className="text-sm text-gray-600 mb-1 block">
                              Period End
                            </label>
                            <div className="flex flex-row space-x-2">
                              <select
                                value={job.is_current ? "9999" : job.period_end_year}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm"
                                onChange={(e) => {
                                  toggleCurrentlyWorking(index, e.target.value === "9999");
                                  handleNestedChange(
                                    "workExperience",
                                    index,
                                    "period_end_year",
                                    e.target.value
                                  )
                                }}
                              >
                                <option value="9999">Present</option>
                                {Array.from(
                                  { length: new Date().getFullYear() - 1990 + 1 },
                                  (_, i) => new Date().getFullYear() - i
                                ).map((year) => (
                                  <option key={year} value={year.toString()}>
                                    {year}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {(job.period_end_year < job.period_start_year) && (
                              <div className="flex flex-rowm items-center mt-2 space-x-1">
                                <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                                <p className="text-red-600 text-sm">
                                  Period end year cannot be before period start year.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Company
                          </label>
                          <input
                            type="text"
                            value={job.institution}
                            onChange={(e) =>
                              handleNestedChange(
                                "workExperience",
                                index,
                                "institution",
                                e.target.value,
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                          />
                          {job.institution === "" && (
                            <div className="flex flex-rowm items-center mt-2 space-x-1">
                              <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                              <p className="text-red-600 text-sm">
                                Company must be filled in.
                              </p>
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Description
                          </label>
                          <textarea
                            value={job.description}
                            onChange={(e) =>
                              handleNestedChange(
                                "workExperience",
                                index,
                                "description",
                                e.target.value,
                              )
                            }
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                          />
                        </div>
                      </div>
                    ) : (
                      <div
                        key={index}
                        className={cn("relative pl-12", {
                          "pb-8": index < displayData.workExperience.length - 1,
                        })}
                      >
                        {/* Timeline line - only show for non-last items */}
                        {index < displayData.workExperience.length - 1 && (
                          <div className="absolute left-3 top-1 bottom-0 w-[2px] bg-blue-100 dark:bg-blue-900/30"></div>
                        )}

                        {/* Icon */}
                        <div className="absolute left-0 top-1">
                          <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center z-10 border-2 border-blue-500 dark:border-blue-400">
                            <BriefcaseIcon className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                          </div>
                        </div>

                        {/* Content */}
                        <div>
                          <div className="mb-1">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1">
                              <h5 className="font-medium text-gray-900 dark:text-gray-100 text-left">
                                {job.role}
                                {" "}
                                {(job.tags && job.tags.length > 0) && (
                                  <>
                                    {filterTagsByContext(
                                      job.tags as TagType[],
                                      "work"
                                    ).map((tag, tagIndex) => (
                                      <ExperienceTag
                                        key={tagIndex}
                                        type={tag}
                                      />
                                    ))}
                                  </>
                                )
                                }
                              </h5>
                              <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0">
                                {job.period_end_year === 9999 ? (
                                  <>
                                    {job.period_start_year} - Present
                                  </>
                                ) : (
                                  <>
                                    {job.period_start_year} - {job.period_end_year}
                                  </>
                                )}
                              </span>
                            </div>
                          </div>
                          {(job.institution || job.location) && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                              {job.institution}{job.location && ` • ${job.location}`}
                            </p>
                          )}
                          {
                            job.description && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {job.description}
                              </p>
                            )
                          }
                        </div >
                      </div >
                    )}
                  </div >
                ))}
            </div >

            {/* Divider after Work Experience */}
            < div className="md:col-span-2 border-t border-gray-200 dark:border-gray-700 my-2 md:my-4" ></div >

            {/* Projects Section */}
            < div className="md:col-span-2" >
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <FileTextIcon className="h-5 w-5 text-blue-500" />
                  Projects
                </h3>
                {isEditing && (
                  <Button
                    type="button"
                    onClick={() => handleAddSection("projects")}
                    variant="outline"
                    className="flex items-center gap-1 text-sm py-1"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Project
                  </Button>
                )}
              </div>

              {
                displayData.projects.map((project, index) => (
                  <div key={index}>
                    {isEditing ? (
                      <div className="py-4 space-y-4">
                        <button
                          onClick={() => handleRemoveSection("projects", index)}
                          className="float-right text-red-500 hover:text-red-700"
                        >
                          <XIcon className="h-5 w-5" />
                        </button>
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">
                            Project Title
                          </label>
                          <Input
                            value={project.name}
                            onChange={(e) =>
                              handleNestedChange(
                                "projects",
                                index,
                                "name",
                                e.target.value,
                              )
                            }
                            className="border-gray-300 text-gray-900"
                          />
                          {project.name === "" && (
                            <div className="flex flex-rowm items-center mt-2 space-x-1">
                              <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                              <p className="text-red-600 text-sm">
                                Project name must be filled in.
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm text-gray-600 mb-1 block">
                              Role
                            </label>
                            <Input
                              value={project.role}
                              onChange={(e) =>
                                handleNestedChange(
                                  "projects",
                                  index,
                                  "role",
                                  e.target.value,
                                )
                              }
                              className="border-gray-300 text-gray-900"
                            />
                            {project.role === "" && (
                              <div className="flex flex-rowm items-center mt-2 space-x-1">
                                <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                                <p className="text-red-600 text-sm">
                                  Role must be filled in.
                                </p>
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="text-sm text-gray-600 mb-1 block">
                              Year
                            </label>
                            <Input
                              value={project.year}
                              onChange={(e) =>
                                handleNestedChange(
                                  "projects",
                                  index,
                                  "year",
                                  e.target.value,
                                )
                              }
                              className="border-gray-300 text-gray-900"
                            />
                            {project.year === "" && (
                              <div className="flex flex-rowm items-center mt-2 space-x-1">
                                <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                                <p className="text-red-600 text-sm">
                                  Year must be filled in.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">
                            Description
                          </label>
                          <Textarea
                            value={project.description}
                            onChange={(e) =>
                              handleNestedChange(
                                "projects",
                                index,
                                "description",
                                e.target.value,
                              )
                            }
                            className="border-gray-300 text-gray-900"
                            rows={3}
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm text-gray-600 mb-1 block">
                              GitHub Link
                            </label>
                            <Input
                              value={project.github_link}
                              onChange={(e) =>
                                handleNestedChange(
                                  "projects",
                                  index,
                                  "github_link",
                                  e.target.value,
                                )
                              }
                              className="border-gray-300 text-gray-900"
                            />
                            {project.github_link === "" && (
                              <div className="flex flex-rowm items-center mt-2 space-x-1">
                                <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                                <p className="text-red-600 text-sm">
                                  GitHub link must be filled in.
                                </p>
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="text-sm text-gray-600 mb-1 block">
                              Live Demo URL
                            </label>
                            <Input
                              value={project.live_demo}
                              onChange={(e) =>
                                handleNestedChange(
                                  "projects",
                                  index,
                                  "live_demo",
                                  e.target.value,
                                )
                              }
                              className="border-gray-300 text-gray-900"
                            />
                            {project.live_demo === "" && (
                              <div className="flex flex-rowm items-center mt-2 space-x-1">
                                <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                                <p className="text-red-600 text-sm">
                                  Live demo URL must be filled in.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">
                            Technologies
                          </label>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {project.tech_stack.map((tech, techIndex) => (
                              <span
                                key={techIndex}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300"
                              >
                                {tech}
                                <button
                                  onClick={() => removeTechnology(index, techIndex)}
                                  className="ml-2 text-gray-500 hover:text-gray-900"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <Input
                              value={newTech}
                              onChange={(e) => setNewTech(e.target.value)}
                              placeholder="Add a technology"
                              className="border-gray-300 text-gray-900"
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  addTechnology(index);
                                }
                              }}
                            />

                            <Button
                              type="button"
                              onClick={() => addTechnology(index)}
                              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                              Add
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div key={index} className={cn("relative pl-12", {
                        "pb-8": index < displayData.projects.length - 1,
                      })}>
                        {/* Timeline line - only show for non-last items */}
                        {index < displayData.projects.length - 1 && (
                          <div
                            className="absolute left-3 top-1 bottom-0 w-[2px]"
                            style={{ backgroundColor: "#f3e8ff" }}
                          ></div>
                        )}

                        {/* Icon */}
                        <div className="absolute left-0 top-1">
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center z-10 border-2"
                            style={{
                              backgroundColor: "#f3e8ff",
                              border: "1px solid #7c3aed",
                            }}
                          >
                            <Code
                              className="h-3 w-3"
                              style={{ color: "#9333ea" }}
                            />
                          </div>
                        </div>

                        {/* Content */}
                        <div>
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 mb-1">
                            <h5 className="font-medium text-gray-900 dark:text-gray-100 text-left">
                              {project.name}
                              {" "}
                              {project.role && (
                                <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 rounded-full">
                                  {project.role}
                                </span>
                              )}
                            </h5>
                            {project.year && (
                              <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0">
                                {project.year}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                            {project.description}
                          </p>

                          {/* Technologies */}
                          {project.tech_stack.length > 0 && (
                            <div className="mb-3 flex flex-row flex-wrap items-center gap-2">
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Technologies:
                              </p>
                              {project.tech_stack.map(
                                (tech, techIndex) => (
                                  <span key={techIndex} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 rounded-md">
                                    {tech}
                                  </span>
                                )
                              )}
                            </div>
                          )}
                          {/* Links section removed as requested */}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              }
            </div >

            {/* Divider after Projects */}
            < div className="md:col-span-2 border-t border-gray-200 dark:border-gray-700 my-4" ></div >

            <div className="md:col-span-2">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <CheckCircleIcon className="h-5 w-5 text-blue-500" />
                  Certifications
                </h3>
                {isEditing && (
                  <Button
                    type="button"
                    onClick={() => handleAddSection("certifications")}
                    variant="outline"
                    className="flex items-center gap-1 text-sm py-1"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Certification
                  </Button>
                )}
              </div>

              {displayData.certifications.map((cert, index) => (
                <div key={index} className="flex items-start gap-4">
                  {isEditing ? (
                    <div className="w-full space-y-4">
                      <button
                        onClick={() => handleRemoveSection("certifications", index)}
                        className="float-right text-red-500 hover:text-red-700"
                      >
                        <XIcon className="h-5 w-5" />
                      </button>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">
                            Certification Title
                          </label>
                          <Input
                            value={cert.name}
                            onChange={(e) => handleNestedChange(
                              "certifications",
                              index,
                              "name",
                              e.target.value,
                            )}
                            className="border-gray-300 text-gray-900"
                          />
                          {cert.name === "" && (
                            <div className="flex flex-rowm items-center mt-2 space-x-1">
                              <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                              <p className="text-red-600 text-sm">
                                Certificate name must be filled in.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label
                            className="text-sm text-gray-600 mb-1 block"
                          >
                            Issuer
                          </label>
                          <Input
                            value={cert.institution}
                            onChange={(e) =>
                              handleNestedChange(
                                "certifications",
                                index,
                                "institution",
                                e.target.value,
                              )
                            }
                            className="border-gray-300 text-gray-900"
                          />
                          {cert.institution === "" && (
                            <div className="flex flex-rowm items-center mt-2 space-x-1">
                              <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                              <p className="text-red-600 text-sm">
                                Issuer must be filled in.
                              </p>
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">
                            Date
                          </label>
                          <Input
                            value={cert.date}
                            onChange={(e) =>
                              handleNestedChange(
                                "certifications",
                                index,
                                "date",
                                e.target.value,
                              )
                            }
                            className="border-gray-300 text-gray-900"
                          />
                          {cert.date === "" && (
                            <div className="flex flex-rowm items-center mt-2 space-x-1">
                              <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                              <p className="text-red-600 text-sm">
                                Date must be filled in.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={`has-expiry-${index}`}
                          checked={cert.is_checked_expired_date}
                          onCheckedChange={() => toggleExpiry(index)}
                        />

                        <label
                          htmlFor={`has-expiry-${index}`}
                          className="text-sm text-gray-400"
                        >
                          Has expiration date
                        </label>
                      </div>
                      {cert.expiration_date !== undefined && (
                        <div className="w-40">
                          <label className="text-sm text-gray-600 mb-1 block">
                            Expiry
                          </label>
                          <Input
                            value={cert.expiration_date}
                            onChange={(e) =>
                              handleNestedChange(
                                "certifications",
                                index,
                                "expiration_date",
                                e.target.value,
                              )
                            }
                            className="border-gray-300 text-gray-900"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div
                        key={index}
                        className={cn("relative pl-12 w-full", {
                          "pb-8": index < displayData.certifications.length - 1,
                        })}
                      >
                        {/* Timeline line - only show for non-last items */}
                        {index < (displayData.certifications || []).length - 1 && (
                          <div
                            className="absolute left-3 top-1 bottom-0 w-[2px]"
                            style={{ backgroundColor: "#fce7f3" }}
                          ></div>
                        )}

                        {/* Icon */}
                        <div className="absolute left-0 top-1">
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center z-10 border-2"
                            style={{
                              backgroundColor: "#fce7f3",
                              border: "0.909091px solid #db2777",
                            }}
                          >
                            <CheckSquare
                              className="h-3 w-3"
                              style={{ color: "#db2777" }}
                            />
                          </div>
                        </div>

                        {/* Content */}
                        <div>
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 mb-1">
                            <h5 className="font-medium text-gray-900 dark:text-gray-100 text-left">
                              {cert.name}
                            </h5>
                            {cert.date && (
                              <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0">
                                {cert.date}
                                {!!cert.expiration_date &&
                                  ` (Expires: ${cert.expiration_date})`}
                              </span>
                            )}
                          </div>
                          {cert.institution && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                              {cert.institution}
                            </p>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Divider after certification */}
            <div className="md:col-span-2 border-t border-gray-200 dark:border-gray-700 my-4"></div>

            {/* Awards Section */}
            <div className="md:col-span-2">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <AwardIcon className="h-5 w-5 text-blue-500" />
                  Awards
                </h3>
                {isEditing && (
                  <Button
                    type="button"
                    onClick={() => handleAddSection("awards")}
                    variant="outline"
                    className="flex items-center gap-1 text-sm py-1"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Award
                  </Button>
                )}
              </div>

              {displayData.awards.map((award, index) => (
                <div key={index}>
                  {isEditing ? (
                    <div className="space-y-4">
                      <button
                        onClick={() => handleRemoveSection("awards", index)}
                        className="float-right text-red-500 hover:text-red-700"
                      >
                        <XIcon className="h-5 w-5" />
                      </button>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">
                            Award Title
                          </label>
                          <Input
                            value={award.name}
                            onChange={(e) =>
                              handleNestedChange(
                                "awards",
                                index,
                                "name",
                                e.target.value,
                              )
                            }
                            className="border-gray-300 text-gray-900"
                          />
                          {award.name === "" && (
                            <div className="flex flex-rowm items-center mt-2 space-x-1">
                              <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                              <p className="text-red-600 text-sm">
                                Award title must be filled in.
                              </p>
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">
                            Date
                          </label>
                          <Input
                            value={award.date}
                            onChange={(e) =>
                              handleNestedChange(
                                "awards",
                                index,
                                "date",
                                e.target.value,
                              )
                            }
                            className="border-gray-300 text-gray-900"
                          />
                          {award.date === "" && (
                            <div className="flex flex-rowm items-center mt-2 space-x-1">
                              <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                              <p className="text-red-600 text-sm">
                                Date must be filled in.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600 mb-1 block">
                          Organization
                        </label>
                        <Input
                          value={award.institution}
                          onChange={(e) =>
                            handleNestedChange(
                              "awards",
                              index,
                              "institution",
                              e.target.value,
                            )
                          }
                          className="border-gray-300 text-gray-900"
                        />
                        {award.institution === "" && (
                          <div className="flex flex-rowm items-center mt-2 space-x-1">
                            <AlertTriangleIcon className="w-4 h-4 text-red-600" />
                            <p className="text-red-600 text-sm">
                              Organization must be filled in.
                            </p>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="text-sm text-gray-600 mb-1 block">
                          Description
                        </label>
                        <Textarea
                          value={award.description}
                          onChange={(e) =>
                            handleNestedChange(
                              "awards",
                              index,
                              "description",
                              e.target.value,
                            )
                          }
                          className="border-gray-300 text-gray-900"
                          rows={3}
                        />
                      </div>
                    </div>
                  ) : (
                    <div key={index} className={cn("relative pl-12", {
                      "pb-8": index < displayData.awards.length - 1,
                    })}>
                      {/* Timeline line - only show for non-last items */}
                      {index < displayData.awards.length - 1 && (
                        <div
                          className="absolute left-3 top-1 bottom-0 w-[2px] bg-blue-100 dark:bg-blue-900/30"
                          style={{ backgroundColor: "#fef3c7" }}
                        ></div>
                      )}

                      {/* Icon */}
                      <div className="absolute left-0 top-1">
                        <div
                          className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center z-10 border-2 border-blue-500 dark:border-blue-400"
                          style={{
                            backgroundColor: "#fef3c7",
                            border: "1.81818px solid #f59e0b",
                          }}
                        >
                          <Trophy
                            className="h-3 w-3 text-blue-600 dark:text-blue-400"
                            style={{ color: "#d97706" }}
                          />
                        </div>
                      </div>

                      {/* Content */}
                      <div>
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 mb-1">
                          <h5 className="font-medium text-gray-900 dark:text-gray-100 text-left">
                            {award.name}
                          </h5>
                          {award.date && (
                            <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0">
                              {award.date}
                            </span>
                          )}
                        </div>
                        {award.institution && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                            {award.institution}
                          </p>
                        )}
                        {award.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {award.description}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div >

          {isEditing && (
            <div className="mt-6 flex justify-end">
              <Button
                type="button"
                variant="outline"
                className="mr-2"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isDisabledButton || isLoading}
              >
                Save Changes
              </Button>
            </div>
          )}
        </form >}


      {
        (!isEditing && !isLoading && !isLoadingCV) && (
          <div
            className="mt-6 flex justify-end"
            data-pol-id="fkj3fp"
            data-pol-file-name="profile-details"
            data-pol-file-type="component"
          >
            <Button
              onClick={() => { setSubmitted(true); onSave(formData) }}
              className="w-full md:w-auto"
              data-pol-id="sxn28f"
              data-pol-file-name="profile-details"
              data-pol-file-type="component"
            >
              Confirm and Continue
            </Button>
          </div>
        )
      }
    </div >
  );
}